import { escapeForGtm, buildHash, parseHash, getBaseUrl } from "./url";

/**
 * Page-level browser-history coordinator. All instances are serialized together under
 * `history.state['kpf-shortcodes']` (keyed by shortcode id) so back/forward restores every shortcode —
 * including cached posts — without a network request. It also owns the URL **fragment** (hash) for
 * `history="true"` shortcodes: a slug-driven, page-global view of the active filter/search state (see
 * {@link buildHash}/{@link parseHash}), built from the union of all history instances so a bookmarked link
 * restores on load. `/page/N/` stays in the path; the fragment rides alongside it.
 */
export default class HistoryManager {
    /** @member {Logger} */
    #logger;

    /** @member {Function} Returns the current ShortcodeInstance[] on the page. */
    #getInstances;

    /** @member {boolean} A fragment sync is queued this tick (collapses bulk updates like Clear into one entry). */
    #syncScheduled = false;

    /** @member {boolean} Whether the queued sync should push a new entry (any push request in the batch wins). */
    #syncPush = false;

    /**
     * @param {Logger} logger
     * @param {Function} getInstances () => ShortcodeInstance[]
     */
    constructor(logger, getInstances) {
        this.#logger = logger;
        this.#getInstances = getInstances;
    }

    /**
     * Reads the saved state map from the current history entry, if any.
     *
     * @returns {object|null} Map of shortcode id → serialized state, or null.
     */
    getSavedState() {
        return (history.state && history.state["kpf-shortcodes"]) || null;
    }

    /**
     * Collects the serializable state of every instance on the page.
     *
     * @returns {object} Map of shortcode id → {settings, cache, requiresReload}.
     */
    #collect() {
        const map = {};
        for(const instance of this.#getInstances()) {
            map[instance.id] = instance.store.serialize();
        }
        return map;
    }

    /**
     * Saves state. With no URL it replaces the current entry; with a URL it pushes (or, when push=false,
     * replaces) an entry at that URL, re-encoding the query/hash for GTM passthrough URLs.
     *
     * @param {string|null} url
     * @param {boolean} push Push a new history entry (true) or replace the current one (false).
     */
    save(url = null, push = true) {
        try {
            if(url === null) {
                history.replaceState({"kpf-shortcodes": this.#collect()}, "");
                return;
            }

            const a = document.createElement("a");
            a.href = url;
            a.search = escapeForGtm(a.search.slice(1));
            a.hash = escapeForGtm(a.hash.slice(1));

            if(push) {
                history.pushState({"kpf-shortcodes": this.#collect()}, "", a.href);
            } else {
                history.replaceState({"kpf-shortcodes": this.#collect()}, "", a.href);
            }
        } catch (e) {
            this.#logger.warn("Failed to save history.");
        }
    }

    /**
     * Clears all saved shortcode state from the current history entry.
     */
    clear() {
        try {
            history.replaceState({"kpf-shortcodes": {}}, "");
        } catch (e) {
            this.#logger.warn("Failed to save history.");
        }
    }

    /* ---- URL fragment (hash) sync — history instances only ---- */

    /** Builds the page-global fragment from the union of all history instances' active selections. */
    #buildFragment() {
        const params = {};
        let search = "";

        for(const instance of this.#getInstances()) {
            if(!instance.store.get("history")) continue;

            const state = instance.hashParams();
            for(const [key, values] of Object.entries(state.params)) {
                if(!(key in params)) params[key] = values; // first history instance owning a key wins
            }
            if(!search && state.search) search = state.search;
        }

        return buildHash({ params, search });
    }

    /**
     * Writes the fragment into the URL. Debounced to a microtask so a burst of updates (e.g. Clear firing a
     * change per adapter) collapses into a single history entry. Preserves any non-plugin query string.
     *
     * @param {boolean} push Push a new entry (discrete change/clear) or replace in place (search typing).
     */
    syncFragment(push = true) {
        this.#syncPush = this.#syncPush || push;

        if(this.#syncScheduled) return;
        this.#syncScheduled = true;

        Promise.resolve().then(() => {
            this.#syncScheduled = false;
            const doPush = this.#syncPush;
            this.#syncPush = false;

            const url = getBaseUrl(true) + location.search + this.#buildFragment();

            this.save(url, doPush);
        });
    }

    /**
     * The parsed fragment restricted to what a given instance recognizes — params whose key slug matches one
     * of the instance's adapters — plus the (page-global) search.
     *
     * @param {ShortcodeInstance} instance
     * @returns {{params: Object<string,string[]>, search: string}}
     */
    fragmentFor(instance) {
        const parsed = parseHash(location.hash);
        const owned = new Set(instance.adapters.map((a) => a.urlSlug));
        const params = {};

        for(const [key, values] of Object.entries(parsed.params)) {
            if(owned.has(key)) params[key] = values;
        }

        return { params, search: parsed.search };
    }

    /**
     * On load, scrolls to the shortcode a filter fragment targets: the `@<form-id>` instance if valid; else
     * the first history instance; if `@` is present but matches nothing, does not scroll. No-op when the
     * fragment carries no filter/search state.
     *
     * @param {number} offset Pixels to subtract from the target's top (header offset).
     */
    scrollToTarget(offset = 0) {
        const parsed = parseHash(location.hash);
        if(Object.keys(parsed.params).length === 0 && !parsed.search) return;

        const historyInstances = this.#getInstances().filter((i) => i.store.get("history"));
        let target;

        // `scroll` is null only when NO `@` marker was present. An empty or unknown `@<id>` counts as
        // "explicitly targeted, but no valid match" → don't scroll (rather than falling back to the first).
        if(parsed.scroll !== null) {
            target = this.#getInstances().find((i) => i.id === parsed.scroll);
            if(!target) return; // @ given but empty / no such shortcode → don't scroll
        } else {
            target = historyInstances[0];
        }

        if(!target) return;

        const top = target.$el.offset().top - offset;
        window.scrollTo({ top: top < 0 ? 0 : top, behavior: "smooth" });
    }

    /**
     * Binds the popstate listener that restores each instance from the navigated-to state map (settings +
     * cached posts — no network request).
     *
     * NOTE: no `hashchange` listener by design. Back/forward fires both popstate and hashchange; popstate
     * already restores from the cached state map, so a hashchange handler would trigger a redundant refetch
     * (double load + "no posts" flash). The only thing this omits is live-applying a manual mid-session
     * address-bar fragment edit — that applies on the next load instead.
     */
    listen() {
        addEventListener("popstate", (event) => {
            const map = event.state && event.state["kpf-shortcodes"];
            if(!map) return;

            for(const instance of this.#getInstances()) {
                const state = map[instance.id];
                if(state) {
                    instance.loadState(state);
                }
            }
        });
    }
}
